import { ApiErrorSchema } from "contracts/api/common/errors";
import type { ErrorCode } from "contracts/enums/api";
import type { ZodType } from "zod";
import { config } from "../config";
import { authorizationHeaders, clearSession } from "./admin";

/**
 * Non-2xx backend response. Carries the backend's own machine-readable error
 * code alongside the status and raw body, so a caller decides what happened
 * from `code` and never from the wording of `message`. `code` is null only
 * when the response body was not the API's error envelope at all.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
    readonly code: ErrorCode | null,
  ) {
    super(`API ${status}: ${body}`);
    this.name = "ApiError";
  }
}

/** The envelope's error code, or null when the body is not one. */
export function apiErrorCode(body: string): ErrorCode | null {
  try {
    const parsed = ApiErrorSchema.safeParse(JSON.parse(body));
    return parsed.success ? parsed.data.error.code : null;
  } catch {
    return null;
  }
}

/**
 * Human-readable message for a caught API failure. Unwraps the backend's
 * `{ error: { message, details } }` envelope (and Zod validation details) so
 * surfaces show "Invalid URL" instead of `API 400: {"error":{...}}`.
 */
export function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    try {
      const parsed = JSON.parse(cause.body) as {
        error?: { message?: string; details?: { message?: string }[] };
      };
      const detail = parsed.error?.details?.[0]?.message;
      const message = parsed.error?.message;
      if (message) {
        return detail && detail !== message ? `${message}: ${detail}` : message;
      }
    } catch {
      // Non-JSON body; fall through to the raw error text.
    }
    if (cause.status === 401) {
      return "Session expired. Sign in again.";
    }
    return cause.message;
  }
  return cause instanceof Error ? cause.message : "Request failed";
}

/**
 * The one JSON transport for all frontend API calls: sends the request,
 * throws ApiError on any non-2xx, and validates the response body through the
 * given contract schema so callers always receive typed data.
 */
export async function request<Output>(
  method: string,
  path: string,
  schema: ZodType<Output>,
  body?: unknown,
): Promise<Output> {
  const headers = authorizationHeaders();
  if (body) {
    headers["content-type"] = "application/json";
  }

  const response = await fetch(config.apiBaseUrl + path, {
    method,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const body = await response.text();
    if (response.status === 401) clearSession();
    throw new ApiError(response.status, body, apiErrorCode(body));
  }
  return schema.parse(await response.json());
}

/**
 * Multipart variant of `request` for file uploads. Lets the browser set the
 * multipart boundary header itself; response validation matches `request`.
 */
export async function uploadRequest<Output>(
  path: string,
  form: FormData,
  schema: ZodType<Output>,
): Promise<Output> {
  const headers = authorizationHeaders();
  const response = await fetch(config.apiBaseUrl + path, {
    method: "POST",
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: form,
  });
  if (!response.ok) {
    const body = await response.text();
    if (response.status === 401) clearSession();
    throw new ApiError(response.status, body, apiErrorCode(body));
  }
  return schema.parse(await response.json());
}

export async function requestBlob(
  path: string,
  signal: AbortSignal,
): Promise<Blob> {
  const response = await fetch(config.apiBaseUrl + path, {
    signal,
    headers: authorizationHeaders(),
  });
  if (!response.ok) {
    if (response.status === 401) clearSession();
    const body = await response.text();
    throw new ApiError(response.status, body, apiErrorCode(body));
  }
  return response.blob();
}
